import { useContext, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';

import { prefersReducedMotion } from '@shigo-ui/ui';

import { fluidDuration, fluidEasing } from '../../../../lib/fluidMotion';
import { AuthStepRecorderContext, authStepDepth, type AuthStep } from './authStep';

const panelTravel = 16;

type AuthPanelProps = {
  step: AuthStep;
  title: string;
  description?: string;
  headerAction?: ReactNode;
  children: ReactNode;
};

/** One step of the auth card. Rendered inside `AuthShell`, which keeps the card itself mounted. */
export function AuthPanel({ step, title, description, headerAction, children }: AuthPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useAuthPanelEntrance(panelRef, step);

  return (
    <div data-auth-step={step} ref={panelRef}>
      {headerAction ? <div className="-mt-3 mb-4">{headerAction}</div> : null}
      <header className="mb-8 space-y-2 text-center">
        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        {description ? (
          <p className="text-muted-foreground mx-auto max-w-lg text-sm leading-6">{description}</p>
        ) : null}
      </header>
      {children}
    </div>
  );
}

/**
 * Plays a short directional entrance when the card changes step: deeper steps arrive from the
 * right, going back arrives from the left, so the path in mirrors the path out. Not on first load
 * (the card itself fades in) and not when the same step remounts. Started in a layout effect so
 * the new step is never painted at rest first; reduced motion keeps only the fade.
 */
function useAuthPanelEntrance(panel: RefObject<HTMLDivElement | null>, step: AuthStep) {
  const recordStep = useContext(AuthStepRecorderContext);

  useLayoutEffect(() => {
    if (!recordStep) return;
    const previous = recordStep(step);
    const element = panel.current;
    if (previous === null || previous === step || !element) return;
    if (typeof element.animate !== 'function') return;

    const direction = authStepDepth[step] >= authStepDepth[previous] ? 1 : -1;
    const reduced = prefersReducedMotion();
    const animation = element.animate(
      reduced
        ? [{ opacity: 0 }, { opacity: 1 }]
        : [
            { opacity: 0, transform: `translateX(${(direction * panelTravel).toString()}px)` },
            { opacity: 1, transform: 'none' },
          ],
      reduced
        ? { duration: 150, easing: 'linear' }
        : { duration: fluidDuration, easing: fluidEasing },
    );
    return () => {
      animation.cancel();
    };
  }, [recordStep, panel, step]);
}
